import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import OutboundWebhooks from "../components/OutboundWebhooks";
import InboundWebhooks from "../components/InboundWebhooks";
import CredentialsVault from "../components/CredentialsVault";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { Copy, Check, PlugsConnected, ShieldCheck, ArrowsClockwise, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

function Box({ label, children, className = "" }) {
  return (
    <div className={`border border-border p-3 ${className}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">
        {label}
      </div>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}

function CopyBtn({ text, label = "Copy", testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); ta.remove();
    }
    setCopied(true); setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button
      onClick={onCopy}
      data-testid={testid}
      className="px-2.5 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold border border-border hover:bg-secondary flex items-center gap-1.5 transition-colors"
    >
      {copied ? <Check size={12} weight="bold" className="text-[#008A00]" /> : <Copy size={12} />}
      {copied ? "Copied" : label}
    </button>
  );
}

export default function Webhooks() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [mcp, setMcp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [showMcpToken, setShowMcpToken] = useState(false);

  const loadInfo = async (reveal = false) => {
    try {
      const [w, m] = await Promise.all([
        api.get(`/webhooks/info${reveal ? "?reveal=true" : ""}`),
        api.get("/mcp-info").catch(() => ({ data: null })),
      ]);
      setInfo(w.data);
      setMcp(m.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { loadInfo(false); }, []);

  // Toggle: reveal both the secret display AND the URLs in the endpoints list.
  // Refetches from the server with ?reveal=true so URLs contain the real token.
  const toggleReveal = async () => {
    if (showSecret) {
      setShowSecret(false);
      await loadInfo(false);
      return;
    }
    setRevealing(true);
    await loadInfo(true);
    setShowSecret(true);
    setRevealing(false);
  };

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-xs uppercase tracking-[0.2em] text-muted-foreground">Loading…</div>;
  }

  const samplePayload = JSON.stringify(info?.sample_payload || {}, null, 2);
  const metaPayload = JSON.stringify(info?.meta_style_payload || {}, null, 2);
  const sampleCurl = info?.endpoints?.[0]
    ? `curl -X POST '${info.endpoints[0].url}' \\\n  -H 'Content-Type: application/json' \\\n  -d '{"customer_name":"Jane Doe","phone":"919876543210","notes":"hot lead"}'`
    : "";

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Integrations"
        title="Webhook ingestion"
        description="Push leads into LeadFlow from Google Ads, Meta Lead Ads, WhatsApp, your CRM or any tool that can POST JSON. Round-robin assigns each new lead to the next active agent."
      />

      {/* Secret */}
      <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border border-border bg-white p-5 lg:col-span-2">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Auth</div>
              <h3 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2">
                <ShieldCheck size={16} weight="fill" className="text-primary" />
                Webhook secret
              </h3>
            </div>
            <CopyBtn text={info?.secret || ""} testid="copy-secret-button" />
          </div>
          <div className="mt-4 flex items-center gap-2">
            <code data-testid="webhook-secret-value"
              className="flex-1 px-3 py-2 bg-secondary font-mono text-sm break-all border border-border">
              {showSecret ? (info?.secret || "—") : "•".repeat(Math.min(40, (info?.secret || "").length))}
            </code>
            <button
              onClick={toggleReveal}
              data-testid="toggle-secret-visibility"
              disabled={revealing}
              className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border border-border hover:bg-secondary disabled:opacity-60"
            >
              {revealing ? "…" : (showSecret ? "Hide" : "Reveal")}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
            Pass this as <code className="font-mono bg-secondary px-1">?token=…</code> on every webhook URL. Rotate by changing
            <code className="font-mono bg-secondary px-1 mx-1">WEBHOOK_SECRET</code>
            in <code className="font-mono">backend/.env</code> and restarting the backend.
          </p>
        </div>

        <div className="border border-border bg-white p-5 flex flex-col">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Assignment</div>
          <h3 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2">
            <ArrowsClockwise size={16} /> Round-robin
          </h3>
          <p className="text-xs text-muted-foreground mt-3 leading-relaxed flex-1">
            New leads cycle through all active agents (oldest-first by hire date). Override per call by
            sending an <code className="font-mono bg-secondary px-1">agent_id</code> in the payload.
          </p>
        </div>
      </div>

      {/* Endpoints */}
      <div className="px-8 pb-6">
        <div className="border border-border bg-white">
          <div className="px-5 py-3 border-b border-border flex items-baseline justify-between">
            <h3 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2">
              <PlugsConnected size={16} /> Endpoints
            </h3>
            <div className="flex items-center gap-3">
              <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${showSecret ? "text-[#008A00]" : "text-muted-foreground"}`}>
                {showSecret ? "· tokens revealed" : "· tokens masked"}
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {info?.endpoints?.length || 0} sources
              </span>
            </div>
          </div>
          <ul className="divide-y divide-border" data-testid="webhook-endpoints">
            {info?.endpoints?.map((ep) => (
              <li key={ep.source} className="px-5 py-4 flex items-center gap-4" data-testid={`webhook-endpoint-${ep.source}`}>
                <div className="w-32 shrink-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Source</div>
                  <div className="text-sm font-semibold mt-0.5">{ep.label}</div>
                </div>
                <code className="flex-1 min-w-0 px-3 py-2 bg-secondary font-mono text-xs break-all border border-border">
                  {ep.url}
                </code>
                <CopyBtn text={ep.url} testid={`copy-url-${ep.source}`} />
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Outbound webhooks */}
      <div className="px-8 pb-6">
        <OutboundWebhooks />
      </div>

      {/* Inbound custom webhooks */}
      <div className="px-8 pb-6">
        <InboundWebhooks />
      </div>

      {/* Credentials vault */}
      <div className="px-8 pb-6">
        <CredentialsVault />
      </div>

      {/* Payloads */}
      <div className="px-8 pb-10 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-border bg-white">
          <div className="px-5 py-3 border-b border-border flex items-baseline justify-between">
            <h3 className="font-display text-base font-semibold tracking-tight">Generic JSON payload</h3>
            <CopyBtn text={samplePayload} testid="copy-sample-json" />
          </div>
          <pre className="px-5 py-4 bg-[#0A0A0A] text-white text-xs font-mono overflow-x-auto leading-relaxed">
{samplePayload}
          </pre>
          <div className="px-5 py-3 border-t border-border text-xs text-muted-foreground">
            Required: <code className="font-mono bg-secondary px-1">customer_name</code>. Everything else optional.
            Synonyms accepted: <code className="font-mono">name</code>, <code className="font-mono">full_name</code>,
            <code className="font-mono">phone_number</code>, <code className="font-mono">whatsapp_number</code>,
            <code className="font-mono">message</code>.
          </div>
        </div>

        <div className="border border-border bg-white">
          <div className="px-5 py-3 border-b border-border flex items-baseline justify-between">
            <h3 className="font-display text-base font-semibold tracking-tight">Meta Lead Ads style</h3>
            <CopyBtn text={metaPayload} testid="copy-meta-json" />
          </div>
          <pre className="px-5 py-4 bg-[#0A0A0A] text-white text-xs font-mono overflow-x-auto leading-relaxed">
{metaPayload}
          </pre>
          <div className="px-5 py-3 border-t border-border text-xs text-muted-foreground">
            We auto-extract from Meta's <code className="font-mono">field_data</code> array. Forward Meta's lead webhook directly with no transform.
          </div>
        </div>
      </div>

      {/* MCP */}
      {mcp?.enabled && (
        <div className="px-8 pb-10">
          <div className="border border-border bg-white">
            <div className="px-5 py-3 border-b border-border flex items-baseline justify-between">
              <h3 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2">
                <Sparkle size={16} weight="fill" className="text-primary" /> Claude · MCP server
              </h3>
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {mcp.tools.length} tools · streamable HTTP
              </span>
            </div>

            <div className="px-5 py-5 space-y-5">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Connect Claude Desktop or claude.ai to LeadFlow. Once connected, you can ask Claude things like
                <span className="font-semibold text-foreground"> "show me all RNR 1 leads from Sadashivanagar this week"</span>
                or <span className="font-semibold text-foreground">"mark lead &lt;id&gt; as converted with note 'paid in full'"</span>.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Box label="Server URL">
                  <code className="font-mono text-xs break-all">{mcp.url}</code>
                  <CopyBtn text={mcp.url} testid="copy-mcp-url" />
                </Box>
                <Box label="Bearer token" className="md:col-span-2">
                  <code className="font-mono text-xs break-all">
                    {showMcpToken ? mcp.token : "•".repeat(Math.min(40, mcp.token.length))}
                  </code>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowMcpToken((v) => !v)}
                      data-testid="toggle-mcp-token"
                      className="px-2.5 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold border border-border hover:bg-secondary"
                    >
                      {showMcpToken ? "Hide" : "Reveal"}
                    </button>
                    <CopyBtn text={mcp.token} testid="copy-mcp-token" />
                  </div>
                </Box>
              </div>

              <div>
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-foreground/70">
                    Claude Desktop config (paste into <code className="font-mono">claude_desktop_config.json</code>)
                  </span>
                  <CopyBtn text={JSON.stringify(mcp.claude_desktop_config, null, 2)} testid="copy-mcp-config" />
                </div>
                <pre className="px-4 py-3 bg-[#0A0A0A] text-white text-xs font-mono overflow-x-auto leading-relaxed">
{JSON.stringify(mcp.claude_desktop_config, null, 2)}
                </pre>
              </div>

              <div>
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-foreground/70 block mb-2">
                  Tools exposed to Claude
                </span>
                <div className="flex flex-wrap gap-2">
                  {mcp.tools.map((t) => (
                    <code key={t} className="font-mono text-[11px] px-2 py-1 bg-secondary border border-border">
                      {t}
                    </code>
                  ))}
                </div>
              </div>

              <p className="text-[11px] text-muted-foreground leading-relaxed border-t border-border pt-3">
                On <strong>claude.ai (web)</strong>, you can also add this as a "Custom Connector" using the URL +
                bearer token directly. On <strong>Claude Desktop</strong>, the config above uses{" "}
                <code className="font-mono bg-secondary px-1">mcp-remote</code> as a bridge — restart Claude after editing.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* cURL */}
      <div className="px-8 pb-12">
        <div className="border border-border bg-white">
          <div className="px-5 py-3 border-b border-border flex items-baseline justify-between">
            <h3 className="font-display text-base font-semibold tracking-tight">Try it from your terminal</h3>
            <CopyBtn text={sampleCurl} testid="copy-curl" />
          </div>
          <pre className="px-5 py-4 bg-[#0A0A0A] text-white text-xs font-mono overflow-x-auto leading-relaxed">
{sampleCurl}
          </pre>
        </div>
      </div>
    </div>
  );
}
